import React, { useEffect, useState } from 'react';
import { Col } from 'react-bootstrap';
import { Button, Fallback, Modal, Text } from 'v3/components';
import { DefaultLoadingComponent } from 'v3/components/List';
import { cooperplaceApi } from 'services/api';
import { useSnackbar } from 'v3/components/Snackbar';
import { RegisterFilter } from './RegisterFilter';
import { AvailableQuestions } from './AvailableQuestions';
import { SelectedQuestions } from './SelectedQuestions';
import {
  AvailableQuestionsActionColumn,
  AvailableQuestionsHeader,
  AvailableQuestionsHeaderActions,
  StyledInfinityScroll,
} from './styles';

const TRAINING_BLOCKED_QUESTION_SLUGS = ['vehicle_plate', 'wagon_plate', 'code'];
const TRAINING_BLOCKED_MESSAGE =
  'Checklist de treinamento não pode incluir perguntas do tipo placa, implemento, código, com subpergunta ou que geram ocorrência.';
const TRAINING_VIDEO_LIMIT = 1;
const TRAINING_VIDEO_LIMIT_MESSAGE =
  'Checklist de treinamento permite apenas 1 pergunta do tipo vídeo.';
const NON_TRAINING_BLOCKED_MESSAGE =
  'Checklist que não seja de treinamento não pode incluir perguntas com resposta correta ou do tipo visualização de vídeo.';

export function QuestionsModal({
  isOpen,
  onClose,
  setQuestions,
  questions,
  isTrainingChecklist = false,
}) {
  const snackbar = useSnackbar();
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState(questions);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    last_page: 1,
  });
  const questionsIds = questions?.map((item) => item.id);
  const selectedQuestionsIds = selectedQuestions?.map((item) => item?.id);

  function getQuestionTypeSlug(question) {
    return String(question?.type?.slug || '').toLowerCase();
  }

  function isVideoVisualizationQuestion(question) {
    return getQuestionTypeSlug(question) === 'video_visualization';
  }

  function hasCorrectAnswerEnabled(question) {
    return Boolean(
      question?.hasCorrectAnswer || question?.subQuestion?.hasCorrectAnswer
    );
  }

  function hasTrainingBlockedRules(question) {
    const questionTypeSlug = getQuestionTypeSlug(question);

    return (
      TRAINING_BLOCKED_QUESTION_SLUGS.includes(questionTypeSlug) ||
      Boolean(question?.subQuestion?.id || question?.hasSubQuestion) ||
      Boolean(question?.createOccurrence)
    );
  }

  function getSelectedTrainingVideoCount(items = selectedQuestions) {
    return (items || []).filter(isVideoVisualizationQuestion).length;
  }

  function mapAvailableQuestionState(item) {
    const questionTypeSlug = getQuestionTypeSlug(item);
    const isTrainingBlocked =
      isTrainingChecklist && hasTrainingBlockedRules(item);
    const isNonTrainingBlocked =
      !isTrainingChecklist &&
      (isVideoVisualizationQuestion(item) || hasCorrectAnswerEnabled(item));
    const selectedTrainingVideoCount = getSelectedTrainingVideoCount();
    const reachedTrainingVideoLimit =
      isTrainingChecklist &&
      isVideoVisualizationQuestion(item) &&
      selectedTrainingVideoCount >= TRAINING_VIDEO_LIMIT;

    if (questionsIds?.includes(item?.id) || selectedQuestionsIds?.includes(item?.id)) {
      return {
        ...item,
        disabled: true,
        disabledReason: 'Pergunta já foi selecionada',
      };
    }

    if (isTrainingBlocked) {
      return {
        ...item,
        disabled: true,
        disabledReason: TRAINING_BLOCKED_MESSAGE,
      };
    }

    if (isNonTrainingBlocked) {
      return {
        ...item,
        disabled: true,
        disabledReason: NON_TRAINING_BLOCKED_MESSAGE,
      };
    }

    if (reachedTrainingVideoLimit) {
      return {
        ...item,
        disabled: true,
        disabledReason: TRAINING_VIDEO_LIMIT_MESSAGE,
      };
    }

    return {
      ...item,
      disabled: false,
      disabledReason: undefined,
    };
  }

  async function hydrateVideoVisualizationQuestions(items = []) {
    const hydrated = await Promise.all(
      (items || []).map(async item => {
        const typeSlug = String(item?.type?.slug || '').toLowerCase();
        const isVideoVisualization = typeSlug === 'video_visualization';
        const hasVideoMedia = !!(
          item?.media?.full_path ||
          item?.media?.fullPath ||
          item?.media?.url
        );

        if (!isVideoVisualization || hasVideoMedia || !item?.id) {
          return item;
        }

        try {
          const { data } = await cooperplaceApi.get(`questions/${item.id}`);
          return {
            ...item,
            media: data?.media || item?.media,
            mediaId: data?.mediaId || item?.mediaId,
          };
        } catch (error) {
          return item;
        }
      })
    );

    return hydrated;
  }

  async function fetchQuestionTypes() {
    try {
      const { data } = await cooperplaceApi.get('questions-types');
      setQuestionTypes(data);
    } catch (error) {
      snackbar.show(
        <Text>Não foi possível carregar todas as opções de seleções. Recarregue a página.</Text>,
        {
          type: 'error',
          showClose: true,
        },
      );
    }
  }

  async function fetchAvailableQuestions(params) {
    try {
      if (params?.page === 1) {
        setLoading(true);
      }
      const { data } = await cooperplaceApi.get('checklists-questions', {
        params: {
          page: params?.page,
          limit: params?.limit,
          question: params?.name,
          typeId: params?.questionType?.map((item) => item.id),
        },
      });

      const hydratedQuestions = await hydrateVideoVisualizationQuestions(
        data?.data || []
      );

      const filtered = hydratedQuestions?.map(mapAvailableQuestionState);
      setAvailableQuestions(old => {
        if (params?.page === 1) {
          return filtered;
        }
        return [...old, ...filtered];
      });
      setPagination(data?.meta);
    } catch (error) {
      // catcher
    } finally {
      setLoading(false);
    }
  }

  function renderItem(item, index) {
    return (
      <AvailableQuestions
        available={item}
        setAvailableQuestions={setAvailableQuestions}
        index={index}
        setSelectedQuestions={setSelectedQuestions}
      />
    );
  }

  function onFilterChange(newFilters) {
    const params = {
      ...newFilters,
      page: 1,
      limit: pagination?.per_page,
    };
    fetchAvailableQuestions(params);
  }

  function handleClose(mappedMerged) {
    setAvailableQuestions([]);
    setSelectedQuestions(mappedMerged || questions);
    onClose();
  }

  function handleSelect() {
    if (
      isTrainingChecklist &&
      getSelectedTrainingVideoCount(selectedQuestions) > TRAINING_VIDEO_LIMIT
    ) {
      snackbar.show(<Text>{TRAINING_VIDEO_LIMIT_MESSAGE}</Text>, {
        type: 'error',
        showClose: true,
      });
      return;
    }

    setQuestions(() => {
      const mappedMerged = selectedQuestions
        ?.filter((obj, index, self) => {
          const indexOfFirstOccurrence = self.findIndex((o) => o.id === obj.id);
          return indexOfFirstOccurrence === index;
        })
        .map((item, idx) => {
          return {
            ...item,
            order: idx + 1,
            required: isTrainingChecklist ? true : item?.required,
          };
        });
      handleClose(mappedMerged);
      return mappedMerged;
    });
  }

  useEffect(() => {
    if (isOpen) {
      fetchAvailableQuestions(pagination);
      fetchQuestionTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedQuestions(questions);
  }, [questions]);

  useEffect(() => {
    setAvailableQuestions(old => old?.map(mapAvailableQuestionState));
  }, [selectedQuestions, isTrainingChecklist]);

  return (
    <Modal
      show={isOpen}
      handleClose={handleClose}
      heading={
        <Text type="regular" color="dark" weight="500">
          Seleção de perguntas
        </Text>
      }
      body={
        <>
          <RegisterFilter onFilterChange={onFilterChange} typeOptions={questionTypes} />
          <Fallback
            fall={availableQuestions?.length === 0}
            component={
              loading ? (
                <DefaultLoadingComponent />
              ) : (
                <Col xs={12} className="d-flex justify-content-center my-5">
                  <Button disabled>
                    <Text>Nenhum registro encontrado.</Text>
                  </Button>
                </Col>
              )
            }
          >
            <Text type="header" color="dark" weight={500}>
              Perguntas disponíveis
            </Text>
            <AvailableQuestionsHeader>
              <Text color="dark" weight={500}>
                Título
              </Text>
              <AvailableQuestionsHeaderActions>
                <Text color="dark" weight={500}>
                  Tipo de pergunta
                </Text>
              </AvailableQuestionsHeaderActions>
              <AvailableQuestionsActionColumn>
                <Text color="dark" weight={500}>
                  Ação
                </Text>
              </AvailableQuestionsActionColumn>
            </AvailableQuestionsHeader>
            <StyledInfinityScroll
              loading={loading}
              data={availableQuestions}
              renderItem={(item, index) => renderItem(item, index)}
              onEndReach={() =>
                fetchAvailableQuestions({
                  page: pagination?.current_page + 1,
                  limit: pagination?.per_page,
                })
              }
              hasMore={pagination?.current_page < pagination?.last_page && !loading}
              scrollThreshold={0.7}
              height={350}
            />
          </Fallback>
          <SelectedQuestions
            setSelectedQuestions={setSelectedQuestions}
            selectedQuestions={selectedQuestions}
            setAvailableQuestions={setAvailableQuestions}
          />
        </>
      }
      footer={
        <Button disabled={selectedQuestions?.length === 0} onClick={handleSelect}>
          <Text>Salvar</Text>
        </Button>
      }
    />
  );
}
